import { useState } from 'react';

import { observer } from 'mobx-react';
import { useAsync } from 'react-use';

import { Loader } from '../../../components/Loader/Loader';
import { CURRENT_PAYMENTS_PROJECT_URL } from '../../../config/constants';
import { logToNewRelic } from '../../../services/debug';
import { firebaseAuth } from '../../../services/firebase';
import { useFlag } from '../../../services/launchDarkly';
import { formatURL } from '../../../utils/formatURL';
import type { ShipCardParams } from '../ExternalPayments';
import styles from '../shared/ExternalPayments.module.scss';

import { useIFrameParams } from './useIFrameParams';
import { useMessagesListener } from './useMessagesListener';
import { useResize } from './useResize';

export const ExternalPaymentsV4 = observer(function ExternalPaymentsV4(props: {
  storeType: 'store' | 'mini-store' | 'subscription';
  isDeposit?: boolean;
  shipping?: ShipCardParams;
}) {
  const { iframeRef } = useResize();

  const [iframeStyle, setIframeStyle] = useState<{ height?: number | string; background?: string }>();
  const [iframeLoading, setIframeLoading] = useState(true);

  const authToken = useAsync(async () => {
    const token = await firebaseAuth.currentUser?.getIdToken();
    if (token) {
      return token;
    } else {
      logToNewRelic('External payments: getFireBaseToken', 'Could not get fire base token', 'fatal');
    }
  }, []);

  const externalPaymentUrl = useFlag<string>('payments_project_url', CURRENT_PAYMENTS_PROJECT_URL);
  const iframeParams = useIFrameParams(props);

  useMessagesListener(setIframeStyle, setIframeLoading, authToken.value, props.storeType === 'mini-store');

  if (!iframeParams || authToken.loading) {
    return <Loader />;
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        allow="payment; camera"
        className={styles.iframe}
        data-loading={iframeLoading}
        data-testid="payments-iframe"
        sandbox="allow-modals allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
        src={`${formatURL(externalPaymentUrl)}?${iframeParams}`}
        style={iframeStyle}
      />
      {iframeLoading && (
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      )}
    </>
  );
});
